/**
 * The one coverage read for a variant
 * (`GET /applications/:id/coverage?variantId=`): requirement and defect
 * coverage with their gap rows, and the proposed-test totals.
 *
 * The dashboard cards, their gap lists and the Proposed tab's coverage bar
 * all read through this hook, so they share one cached response and cannot
 * show numbers from two reads. `invalidateCoverage` in `lib/issueCoverage`
 * is the one way to mark it out of date. Lives in the shared hooks folder
 * because two pages use it.
 *
 * Never treated as fresh (`staleTime: 0`): coverage is worked out on every
 * read by design, and a run can finish on another page or variant without
 * anything here hearing about it. Opening a screen therefore always asks
 * again, while two components on one screen still share the one response.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { applicationsAPI } from '@/lib/api'
import { COVERAGE_QUERY_KEY } from '@/lib/issueCoverage'
import type { VariantCoverage } from '@/types'

export function useCoverage(
  applicationId: string | null | undefined,
  variantId: string | null | undefined,
): UseQueryResult<VariantCoverage> {
  return useQuery({
    queryKey: [COVERAGE_QUERY_KEY, applicationId, variantId],
    queryFn: () => {
      if (!applicationId || !variantId) {
        throw new Error('useCoverage: applicationId and variantId required')
      }
      return applicationsAPI.coverage(applicationId, variantId)
    },
    enabled: !!applicationId && !!variantId,
    staleTime: 0,
  })
}
